import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useBalloonEventsData} from '../contexts/EventData';
import {usePartners} from '../contexts/Partners';
import {ZeppelinContainer} from '../components/ZeppelinContainer';
import {ZeppelinContainerV2} from '../components/ZeppelinContainerV2';
import {HotspotsLayer} from '../components/HotspotsLayer';
import {AllBalloonsLayer} from '../components/AllBalloonsLayer';
import {HOTSPOT_DISPLAY_MODE} from '../hooks/useMapHotspots';
import {useBalloonEventsOptions} from '../contexts/EmbedOptions';
import {Spinner, useQuery} from 'react-ui';
import {EventsList} from '../components/EventsList';
import {FilterToolbar} from './blocks/FilterToolbar';
import {BottomSheet} from '../components/BottomSheet';
import dayjs from 'dayjs';
import {BalloonDetails} from "../components/EventDetails.tsx";
import {QueryFeed} from "../components/QueryBadge.tsx";
import {ProfileDetailPage} from "./ProfileDetailPage.tsx";
import {UserScopeHeader} from "../components/UserScopeHeader";
import {useUserScope} from "../contexts/UserScope";
import {PartnerScopeDetailPage} from "./PartnerScopeDetailPage.tsx";
import {PartnerScopeHeader} from "../components/PartnerScopeHeader";
import {usePartnerScope} from "../contexts/PartnerScope";
import {BannerContainerV2} from '../components/BannerContainerV2';
import {AssistantLauncher} from '../components/AssistantLauncher';

/**
 * Mobile-optimized UI with bottom sheet for events list
 * Used when viewport width < 768px
 * Features:
 * - Fixed FilterToolbar at top
 * - Swipeable BottomSheet with EventsList
 * - Maximized map visibility
 */
const BANNER_HALF_OFFSET_PX = 80;
// In profile-scoped mode (user/partner) the scope header sits above the events
// carousel, pushing it down so it's only half-visible at the normal `half`
// snap. Lift the sheet by the header's height so the events show fully.
const PROFILE_HALF_OFFSET_PX = 100;

export default function BalloonEventsMobileUI() {
    const {selectedEvent, loading, events, allFilteredEvents, selectEvent} = useBalloonEventsData();
    const {addQuery, query, queries, removeQueryById} = useQuery();
    const memoizedQuery = useMemo(() => query, [query]);
    const memoizedQueries = useMemo(() => queries, [queries]);
    const [mobileBannerCount, setMobileBannerCount] = useState(0);
    // A scope header is rendered above the list when scoped to a user, or to a
    // partner that has events (PartnerScopeHeader only renders when hasEvents).
    const {slug: scopedUserSlug} = useUserScope();
    const {code: scopedPartnerCode, hasEvents: partnerHasEvents} = usePartnerScope();
    const isProfileScoped = Boolean(scopedUserSlug) || Boolean(scopedPartnerCode && partnerHasEvents);
    const {
        useDateSearch,
        useCategorySearch,
        useTextSearch,
        showCreatorLink,
        creatorURL,
        showZeppelins,
        hideUI,
    } = useBalloonEventsOptions();

    const {sponsors, regionalPartners} = usePartners();

    const [showSpinner, setShowSpinner] = useState(false);

    const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        if (loading) {
            timerRef.current = setTimeout(() => setShowSpinner(true), 1000);
        } else setShowSpinner(false);
    }, [loading]);

    const handleSetSearchText = (text: string) => addQuery({text, id: 'text'});

    const MemoizedSpinner = React.memo(({show}: { show: boolean }) => {
        if (!show) return null;
        return (
            <div className="absolute top-[50pt] left-1/2 transform -translate-x-1/2 animate-opacity-up z-35">
                <Spinner/>
            </div>
        );
    });

    // hideUI: render only the balloon layer over the map — no bottom sheet,
    // filter toolbar, banners, zeppelins, detail pages or query feed.
    if (hideUI) {
        return HOTSPOT_DISPLAY_MODE === 'dots' ? <AllBalloonsLayer /> : <HotspotsLayer />;
    }

    return (
        <div className="mobile-ui w-full h-full">
            <MemoizedSpinner show={showSpinner}/>
            <ProfileDetailPage/>
            <PartnerScopeDetailPage/>
            {selectedEvent && (
                <BalloonDetails selectedEvent={selectedEvent}/>
            )}
            {/* Fixed FilterToolbar at top */}
            {/* bg-white/0 backdrop-blur-md*/}
            <div className="balloon-map-topbar absolute top-0 left-0 right-0  z-90 ">
                <FilterToolbar
                    useCategorySearch={useCategorySearch || false}
                    useDateSearch={useDateSearch || false}
                    memoizedQuery={memoizedQuery}
                    callbackfn={(d) => dayjs(d)}
                    selectedDates={(d) => addQuery({id: 'dates', dates: d.map((date) => date.toDate())})}
                    useTextSearch={useTextSearch || false}
                    loading={loading}
                    events={allFilteredEvents}
                    selectEvent={selectEvent}
                    selectedEvent={selectedEvent}
                    searchText={handleSetSearchText}
                    searchText1={(text) => addQuery({text, id: 'text'})}
                    showCreatorLink={showCreatorLink || false}
                    creatorURL={creatorURL}
                />
                
            </div>
            
            {/* Bottom Sheet with EventsList */}
            <BottomSheet
                initialState="half"
                persist={false}
                expandTrigger={selectedEvent}
                extraHalfPx={(mobileBannerCount > 0 ? BANNER_HALF_OFFSET_PX : 0) + (isProfileScoped ? PROFILE_HALF_OFFSET_PX : 0)}
            >
                <BannerContainerV2
                    variant="desktop"
                    className="banners flex flex-row items-end px-0 gap-3 absolute top-[-65px] z-10 left-3"
                />
                {/* Assistant launcher floated above the sheet on the right, the same
                    child-of-the-sheet pattern the banners use so it tracks the sheet. Sits
                    above the mapbox geolocate control (which is anchored just above the
                    sheet on the right) rather than overlapping it. */}
                <AssistantLauncher className="absolute -top-24 right-3 z-20" />
                <UserScopeHeader />
                <PartnerScopeHeader />
                <EventsList
                    partners={regionalPartners}
                    hasMore={false}
                    isLoading={loading}
                    onLoadMore={() => void 0}
                    events={events}
                />
            </BottomSheet>

            {/* Zeppelin sponsors */}
            {showZeppelins && <ZeppelinContainer sponsors={sponsors}/>}
            {showZeppelins && <ZeppelinContainerV2/>}
            {HOTSPOT_DISPLAY_MODE === 'dots' ? <AllBalloonsLayer /> : <HotspotsLayer />}
            <div className="hidden flex-col z-50 absolute bottom-0 w-full pointer-events-none">
                <div className="flex self-end md:mr-8 mr-2">
                    <QueryFeed queries={memoizedQueries} removeQuery={removeQueryById}/>
                </div>
            </div>
        </div>
    );
}
